import { Timeout } from "@utils/helpers/timeout"

/**
 * Configuration options for the longPress directive.
 */
export interface LongPressOptions {
    /**
     * Callback function executed **upon pointer release** after a successful long press
     * duration has been met.
     *
     * **Important:** Execute security-sensitive actions (like `navigator.clipboard.write`)
     * here, as this callback runs within the context of the pointer release event
     * (`pointerup`, `pointerleave`, `pointercancel`), which is more likely to have
     * the necessary "user activation" permissions.
     */
    callback?: () => void

    /**
     * Optional callback function executed *immediately* when the long press timer fires
     * (i.e., after `duration` ms, while the pointer is potentially still down).
     * Use this for immediate feedback like vibration (`navigator.vibrate()`) or
     * visual cues (style changes).
     *
     * **Avoid** security-sensitive actions here, as this callback runs asynchronously
     * via `setTimeout` and may lack "user activation" permissions.
     */
    onTimerFire?: () => void

    /**
     * Callback function executed on a short press (pointer released *before*
     * the `duration` timeout). Security-sensitive actions are generally safe here
     * as it runs directly within the pointer release event handler.
     */
    callbackShortpress?: () => void

    /**
     * If `true`, stops event propagation (`e.stopPropagation()`) on the pointer
     * release event *only if* it was a successful long press.
     * Defaults to `false`.
     */
    stopPropagation?: boolean

    /**
     * Duration in milliseconds the pointer must be held down to qualify as a long press.
     * Defaults to `500`.
     */
    duration?: number

    /**
     * Optional: Name of a `CustomEvent` to dispatch on the `node` immediately
     * when the long press timer fires (`onTimerFire` timing). The event detail will be null.
     */
    event?: string
}

/**
 * Attaches long press detection behavior to an HTML element.
 * Differentiates between short presses and long presses based on a configurable duration.
 * Handles user activation requirements for sensitive APIs like clipboard access by
 * triggering the main long press callback upon pointer release.
 *
 * @param node The HTML element to attach the listeners to.
 * @param options Configuration options for the long press behavior.
 * @returns An object with a `destroy` method to remove listeners and clean up resources.
 *
 * @example
 * const button = document.getElementById('copyButton');
 *
 * const longPressAction = longPress(button, {
 *   duration: 700, // 700ms for long press
 *   onTimerFire: () => {
 *     // Give haptic feedback when timer fires
 *     navigator.vibrate?.(50);
 *     button.style.opacity = '0.7';
 *     console.log('Long press timer fired...');
 *   },
 *   callback: async () => {
 *     // Actual action on pointer release after long press
 *     try {
 *       await navigator.clipboard.writeText("Copied on long press!");
 *       console.log('Text copied successfully!');
 *       button.style.opacity = '1'; // Reset style
 *     } catch (err) {
 *       console.error('Failed to copy text:', err);
 *       alert('Could not copy text.');
 *       button.style.opacity = '1'; // Reset style
 *     }
 *   },
 *   callbackShortpress: () => {
 *     console.log('Short press detected.');
 *     // Maybe perform a different action for short press
 *   },
 *   stopPropagation: true // Stop propagation after a successful long press callback
 * });
 *
 * // To remove the behavior later:
 * // longPressAction.destroy();
 */
export function longPress(node: HTMLElement, options: LongPressOptions): { destroy: () => void } {
    const {
        callback,
        onTimerFire,
        callbackShortpress,
        stopPropagation = false,
        duration = 500,
        event,
    } = options

    const timeout = new Timeout(duration)

    // --- State Variables ---
    let isPointerDown = false // Is a primary pointer currently pressed down on this element?
    let longPressThresholdMet = false // Has the timeout duration been reached during the current press?
    let shouldStopPropagationOnRelease = false // Should propagation be stopped *if* this ends up being a long press?

    // --- Event Handlers ---

    function pointerDownHandler(e: PointerEvent): void {
        // Only react to the main pointer button (e.g., left mouse, single touch)
        if (e.button !== 0 || !node.contains(e.target as Node)) {
            // Also check if the event target is within the node, in case of nested elements and event bubbling
            return
        }

        isPointerDown = true
        longPressThresholdMet = false // Reset for the new interaction
        shouldStopPropagationOnRelease = false // Reset for the new interaction

        // Schedule the action for when the timer completes
        timeout.overwrite(() => {
            // This callback runs only if the pointer stays down long enough

            // Double-check if pointer is still down when timer fires (safety)
            if (!isPointerDown) return

            // Mark that the duration threshold was met
            longPressThresholdMet = true

            // Execute the immediate feedback callback (if provided)
            // Safe for non-restricted actions like vibration or style changes.
            try {
                onTimerFire?.()
            } catch (err) {
                console.error("Error during longPress onTimerFire callback:", err)
            }

            // Dispatch custom event immediately (if specified)
            if (event) {
                node.dispatchEvent(new CustomEvent(event))
            }

            // Decide if propagation should be stopped on release *if* this completes as a long press
            if (stopPropagation) {
                shouldStopPropagationOnRelease = true
            }

            // --- Note: The main `callback` is NOT called here ---
        })
    }

    function interactionEndHandler(e: PointerEvent): void {
        // Only handle if a pointerdown interaction started on this element
        if (!isPointerDown) {
            return
        }

        // Capture the state *before* resetting flags, as we need it to decide the action
        const wasLongPress = longPressThresholdMet

        // --- Cleanup and State Reset ---
        timeout.cancel() // Crucial: Cancel any pending timeout immediately
        isPointerDown = false
        longPressThresholdMet = false
        // `shouldStopPropagationOnRelease` is used below then implicitly reset

        // --- Determine Action based on captured state ---
        if (wasLongPress) {
            // --- Long Press Completed ---
            // Execute the main callback (intended for restricted actions)
            // This runs in the context of pointerup/leave/cancel, preserving user activation
            try {
                callback?.()
            } catch (err) {
                console.error("Error during longPress callback:", err)
                // Inform the user? Revert UI changes?
            }

            // Stop propagation if configured and applicable for this event type
            if (shouldStopPropagationOnRelease && typeof e.stopPropagation === "function") {
                e.stopPropagation()
                // console.log(`Propagation stopped on ${e.type} after long press`); // For debugging
            }
        } else {
            // --- Short Press ---
            // Execute the short press callback
            try {
                callbackShortpress?.()
            } catch (err) {
                console.error("Error during longPress callbackShortpress:", err)
            }
        }

        // Implicitly reset for the next interaction cycle
        shouldStopPropagationOnRelease = false
    }

    // --- Attach Listeners ---
    node.addEventListener("pointerdown", pointerDownHandler)
    // Use the same handler for all interaction end scenarios
    node.addEventListener("pointerup", interactionEndHandler)
    node.addEventListener("pointerleave", interactionEndHandler)
    node.addEventListener("pointercancel", interactionEndHandler) // Important for interruptions

    // --- Return destroy function ---
    return {
        destroy() {
            timeout.cancel() // Ensure timeout is cleared
            node.removeEventListener("pointerdown", pointerDownHandler)
            node.removeEventListener("pointerup", interactionEndHandler)
            node.removeEventListener("pointerleave", interactionEndHandler)
            node.removeEventListener("pointercancel", interactionEndHandler)
            // Reset state just in case destroy is called mid-interaction
            isPointerDown = false
            longPressThresholdMet = false
            shouldStopPropagationOnRelease = false
            // console.log("Long press listener destroyed"); // For debugging
        },
    }
}

/**
 * Configuration options for the doublePoint function.
 */
interface DoublePointOptions {
    /** Function to execute on successful double point detection. */
    callback?: () => void
    /**
     * The maximum time in milliseconds allowed between two pointerup events
     * to be considered a double point.
     * @default 500
     */
    timer?: number
    /**
     * Optional name of a CustomEvent to dispatch on the node
     * upon successful double point detection.
     */
    event?: string
}

/**
 * Attaches double-pointer (e.g., double-click, double-tap) detection
 * to an HTML element. Executes a callback or dispatches an event
 * if two pointerup events occur within a specified time window.
 *
 * @param node - The HTML element to attach the listener to.
 * @param options - Configuration options for the double point detection.
 * @returns An object with a `destroy` method for cleanup.
 *
 * @example
 * const button = document.getElementById('myButton');
 * const dblClickAction = doublePoint(button, {
 *   callback: () => console.log('Double Clicked!'),
 *   timer: 300 // 300ms window for double click
 * });
 *
 * // Later, to remove the listener:
 * // dblClickAction.destroy();
 */
export function doublePoint(
    node: HTMLElement,
    options: DoublePointOptions, // Use the interface for options
) {
    // Use the interface for the return type
    // Define the time window for detecting a double point
    const timeWindow = options.timer ?? 500 // Default to 500ms

    // Create a Timeout helper instance. The timeout duration determines the double-point window.
    const timeout = new Timeout(timeWindow)

    /**
     * Handles the pointerup event to detect double points.
     * @param e - The pointer event object.
     */
    function pointerupHandler(e: PointerEvent): void {
        // Ignore clicks from non-primary buttons (e.g., right-click)
        if (e.button !== 0) return

        // Check if a timeout from a *previous* pointerup is still pending.
        // This means the current pointerup occurred within the `timeWindow`.
        if (timeout.pending) {
            // --- Double Point Detected ---

            // Cancel the pending timeout immediately.
            timeout.cancel()

            // Execute the callback if provided
            if (options.callback) {
                options.callback()
            }

            // Dispatch a custom event if specified
            if (options.event) {
                node.dispatchEvent(new CustomEvent(options.event))
            }

            // Note: The double-point sequence is complete. The next pointerup
            // will start a new potential sequence.
        } else {
            // --- First Point or Too Slow ---

            // Start (or overwrite) the timeout. If another pointerup occurs before
            // this timeout completes, the `if (timeout.pending)` block above will execute.
            // The timeout itself doesn't need to *do* anything when it expires,
            // only indicate its pending state via `timeout.pending`.
            timeout.overwrite(() => {
                /* No operation needed on timeout completion */
            })
        }
    }

    // Attach the event listener to the node
    node.addEventListener("pointerup", pointerupHandler)

    // Return the destroy function for cleanup
    return {
        /**
         * Removes the event listener and cancels any pending timeout.
         */
        destroy(): void {
            // Ensure listener is removed
            node.removeEventListener("pointerup", pointerupHandler)
            // Ensure any pending timeout waiting for a second click is cleared
            timeout.cancel()
        },
    }
}
